import React from 'react'
import { Link } from 'react-router'

import { ArrowRight } from 'lucide-react'

import type { AssessmentPrioritario } from '@/types/dashboard_stats.types'
import { cn } from '@/utils/cn'

interface PrioritariosTableProps {
  items: AssessmentPrioritario[]
}

const FASE_LABELS: Record<string, string> = {
  'current-level': 'Nivel actual',
  'gap-analysis': 'Brechas',
  recommendations: 'Recomendaciones',
  planning: 'Planificación'
}

function ScoreBadge({ score }: { score: number }) {
  const color =
    score >= 70
      ? 'bg-red-100 text-red-700'
      : score >= 40
        ? 'bg-orange-100 text-orange-700'
        : 'bg-gray-100 text-gray-600'
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-bold', color)}>
      {score}
    </span>
  )
}

export const PrioritariosTable: React.FC<PrioritariosTableProps> = ({ items }) => {
  if (items.length === 0) {
    return <p className="text-sm text-gray-400 italic">No hay assessments en curso actualmente.</p>
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            <th className="pb-2 text-left">Assessment</th>
            <th className="pb-2 text-left hidden md:table-cell">Organización</th>
            <th className="pb-2 text-left hidden lg:table-cell">Fase</th>
            <th className="pb-2 text-center">Sin actividad</th>
            <th className="pb-2 text-center hidden sm:table-cell">Vence</th>
            <th className="pb-2 text-center">Urgencia</th>
            <th className="pb-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {items.map((item) => (
            <tr key={item.assessment_id} className="hover:bg-gray-50">
              <td className="py-2.5 pr-3">
                <span className="font-medium text-gray-900 truncate max-w-[180px] block">
                  {item.nombre}
                </span>
                <span className="text-xs text-gray-400">{item.codigo}</span>
              </td>
              <td className="py-2.5 pr-3 hidden md:table-cell text-gray-600">
                {item.organizacion_nombre}
              </td>
              <td className="py-2.5 pr-3 hidden lg:table-cell text-gray-500 text-xs">
                {FASE_LABELS[item.fase_actual] ?? item.fase_actual}
              </td>
              <td className="py-2.5 pr-3 text-center text-xs text-gray-600">
                {item.dias_sin_actividad}d
              </td>
              <td className="py-2.5 pr-3 text-center text-xs hidden sm:table-cell">
                {item.fecha_fin_estimada ? (
                  <span
                    className={
                      item.dias_para_vencer !== null && item.dias_para_vencer < 0
                        ? 'text-red-600 font-semibold'
                        : item.dias_para_vencer !== null && item.dias_para_vencer <= 7
                          ? 'text-orange-600 font-medium'
                          : 'text-gray-500'
                    }
                  >
                    {item.dias_para_vencer !== null && item.dias_para_vencer < 0
                      ? `Hace ${Math.abs(item.dias_para_vencer)}d`
                      : item.dias_para_vencer !== null
                        ? `En ${item.dias_para_vencer}d`
                        : item.fecha_fin_estimada}
                  </span>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </td>
              <td className="py-2.5 pr-3 text-center">
                <ScoreBadge score={item.score_urgencia} />
              </td>
              <td className="py-2.5 text-right">
                <Link
                  to={`/assessments/${item.assessment_id}/dashboard`}
                  className="inline-flex items-center text-primary-600 hover:text-primary-800"
                >
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
